import { unassignGroupFromRoute } from "@/api/Routes";
import { deallocateShiftsFromActivityOccurrence } from "@/api/Activities";
import { useErrorMessageAlert } from "@/Utils/ErrorUtils";
import { useDragAndDropContext } from "./DragAndDropContext";
import { useDragAndDropMutation } from "./dragAndDropUtils";
import * as Sentry from "@sentry/react";
import { useLingui } from "@lingui/react/macro";
import { isGroup } from "@medoma/models/activities";
import { unassignGroupFromShift } from "@/api/Shifts";

export const useDropToSidebar = () => {
  const { t } = useLingui();
  const { displayErrorMessageAlert } = useErrorMessageAlert();
  const { drag } = useDragAndDropContext();

  const { mutate: unassignVisitMutation, isPending: isUnassigningVisit } =
    useDragAndDropMutation({
      mutationFn: ({
        visitId,
        routeId,
      }: {
        visitId: string;
        routeId: string;
      }) => {
        return unassignGroupFromRoute(visitId, routeId);
      },
      errorMessage: t`Det gick inte att ta bort besöket från rutten.`,
    });

  const {
    mutate: deallocateActivityMutation,
    isPending: isDeallocatingActivity,
  } = useDragAndDropMutation({
    mutationFn: ({
      shiftId,
      activityId,
      occurrenceId,
    }: {
      shiftId: number;
      activityId: string;
      occurrenceId: string;
    }) => {
      return deallocateShiftsFromActivityOccurrence(
        [shiftId],
        activityId,
        occurrenceId,
      );
    },
    errorMessage: t`Det gick inte att ta bort aktiviteten från arbetspasset.`,
  });

  const { mutate: unassignGroupMutation, isPending: isUnassigningGroup } =
    useDragAndDropMutation({
      mutationFn: ({
        groupId,
        shiftId,
      }: {
        groupId: string;
        shiftId: number;
      }) => unassignGroupFromShift(groupId, shiftId),
      errorMessage: t`Det gick inte att ta bort besöket från arbetspasset.`,
    });

  const handleDrop = () => {
    if (!drag) {
      Sentry.captureException(
        new Error("A dropped item is missing the mandatory 'drag'."),
      );
      displayErrorMessageAlert(
        t`Något gick fel. Prova igen. Om felet kvarstår, hör av dig till teknisk support.`,
      );
      return;
    }

    switch (drag.source) {
      // Visit from a route to be unplanned
      case "route":
        return unassignVisitMutation({
          visitId: drag.item.id,
          routeId: drag.routeId,
        });
      // A lane's tile stands for that shift's assignment alone: work assigned to
      // several shifts shows up in one lane per shift, so unplanning one tile
      // pulls back that shift and leaves the other assignees planned, the way a
      // visit dragged off one of its routes stays on the others.
      case "shift-lane":
        return isGroup(drag.item)
          ? unassignGroupMutation({
              groupId: drag.item.id,
              shiftId: drag.shiftId,
            })
          : deallocateActivityMutation({
              shiftId: drag.shiftId,
              activityId: drag.item.activityId,
              occurrenceId: drag.item.id,
            });
      case "sidebar":
        // What the sidebar holds is unplanned already.
        return undefined;
    }
  };

  return {
    handleDrop,
    isPending:
      isUnassigningVisit || isDeallocatingActivity || isUnassigningGroup,
  };
};
